import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Alert, AlertDescription } from '../ui/alert';
import { Switch } from '../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { cn } from '../../lib/utils';
import {
  AlertTriangle,
  Check,
  Copy,
  ExternalLink,
  Loader2,
  Plus,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { apiClient, GCPBootstrapResponse, GCPConnection, cleanErrorMessage } from '../../api/client';
import { ReactComponent as GCPIcon } from '../../assets/icons/gcp.svg';

// GCPCloudTab renders the Integrations -> Cloud -> Google Cloud panel.
//
// Onboarding is keyless: the customer runs a generated Cloud Shell script that
// creates a least-privilege service account and grants Kestrel permission to
// impersonate it. No service-account keys are ever created or uploaded, which is
// why this flow has no secret input.
export function GCPCloudTab() {
  const [connections, setConnections] = useState<GCPConnection[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  const [showConnectModal, setShowConnectModal] = useState(false);
  const [connectionToDelete, setConnectionToDelete] = useState<GCPConnection | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadConnections = useCallback(async () => {
    try {
      setIsLoading(true);
      setLoadError('');
      const result = await apiClient.getGCPConnections();
      setConnections(result || []);
    } catch (err) {
      setLoadError(cleanErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  const handleDelete = async () => {
    if (!connectionToDelete) return;
    try {
      setIsDeleting(true);
      await apiClient.deleteGCPConnection(connectionToDelete.id);
      setConnectionToDelete(null);
      await loadConnections();
    } catch (err) {
      setLoadError(cleanErrorMessage(err));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-6 p-6 border-2 border-border rounded-lg bg-card">
      <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b">
        <div className="flex items-center gap-3">
          <GCPIcon className="h-8 w-8" />
          <div>
            <h2 className="text-2xl font-bold">Google Cloud</h2>
            <p className="text-sm text-muted-foreground">
              Manage GCP projects for cost, compute, security, and data automation
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={() => { loadConnections(); }} disabled={isLoading}>
          <RefreshCw className={cn("h-4 w-4 mr-2", isLoading && "animate-spin")} />
          Refresh
        </Button>
      </div>

      {loadError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="break-words">{loadError}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle>Connected Projects</CardTitle>
              <CardDescription className="mt-1.5">
                Kestrel impersonates a least-privilege service account in each project. No keys are stored.
              </CardDescription>
            </div>
            {connections.length > 0 && (
              <Button variant="outline" onClick={() => setShowConnectModal(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Connect Project
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              <span className="text-sm">Loading projects...</span>
            </div>
          ) : connections.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-sm text-muted-foreground mb-4">
                No GCP projects connected. Connect a project to enable GCP triggers and action blocks in Workflows.
              </p>
              <Button
                variant="outline"
                className="bg-white text-black hover:bg-white/90 dark:bg-white dark:text-black dark:hover:bg-white/90"
                onClick={() => setShowConnectModal(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Connect Project
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-muted/50 p-4 rounded-lg border mb-4">
                <p className="text-sm text-muted-foreground">
                  Connected projects unlock the GCP trigger and action blocks in Workflows. Cost blocks
                  additionally need a BigQuery billing export, since GCP has no general-purpose detailed
                  cost API.
                </p>
              </div>

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Project ID</TableHead>
                      <TableHead>Region</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Billing Export</TableHead>
                      <TableHead>Connected</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {connections.map(conn => (
                      <GCPConnectionRow
                        key={conn.id}
                        connection={conn}
                        onDelete={() => setConnectionToDelete(conn)}
                        onChanged={loadConnections}
                      />
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <GCPConnectModal
        open={showConnectModal}
        onClose={() => setShowConnectModal(false)}
        onConnected={async () => {
          setShowConnectModal(false);
          await loadConnections();
        }}
      />

      <Dialog open={!!connectionToDelete} onOpenChange={open => !open && setConnectionToDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete GCP Connection</DialogTitle>
            <DialogDescription>
              Are you sure you want to disconnect GCP project{' '}
              <code className="bg-muted px-1 py-0.5 rounded text-xs">
                {connectionToDelete?.project_id}
              </code>?
              Kestrel will stop polling it and workflows scoped to it will no longer run. The service
              account and IAM bindings stay in your project; delete them there if you want them gone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConnectionToDelete(null)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Connection'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// GCPConnectionRow renders one connected project as a table row, matching the
// AWS connection table. The billing-export cell is inline-editable because a
// missing export is the most common reason cost blocks return no data.
function GCPConnectionRow({
  connection,
  onDelete,
  onChanged,
}: {
  connection: GCPConnection;
  onDelete: () => void;
  onChanged: () => void;
}) {
  const [isEditingExport, setIsEditingExport] = useState(false);
  const [exportTable, setExportTable] = useState(connection.billing_export_table || '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const saveExport = async () => {
    try {
      setIsSaving(true);
      setError('');
      await apiClient.updateGCPBillingExport(connection.id, exportTable.trim());
      setIsEditingExport(false);
      onChanged();
    } catch (err) {
      setError(cleanErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  // Mirrors getConnectionStatusBadge in the AWS tab so both clouds read the same.
  const statusBadge = () => {
    switch (connection.connection_status) {
      case 'connected':
        return (
          <Badge variant="default" className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 font-normal text-xs">
            Connected
          </Badge>
        );
      case 'failed':
        return (
          <Badge variant="default" className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 font-normal text-xs">
            Failed
          </Badge>
        );
      case 'pending':
        return (
          <Badge variant="secondary" className="font-normal text-xs">
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            Pending
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary" className="font-normal text-xs">
            {connection.connection_status}
          </Badge>
        );
    }
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  return (
    <>
      <TableRow>
        <TableCell className="font-mono text-sm">{connection.project_id}</TableCell>
        <TableCell>{connection.default_region}</TableCell>
        <TableCell>{statusBadge()}</TableCell>
        <TableCell>
          <div className="flex items-center gap-2">
            {connection.billing_export_table ? (
              <Badge variant="default" className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 font-normal text-xs">
                Configured
              </Badge>
            ) : (
              <Badge variant="secondary" className="font-normal text-xs">
                Not configured
              </Badge>
            )}
            {!isEditingExport && (
              <Button variant="ghost" size="sm" onClick={() => setIsEditingExport(true)}>
                {connection.billing_export_table ? 'Change' : 'Add'}
              </Button>
            )}
          </div>
        </TableCell>
        <TableCell>{formatDate(connection.created_at)}</TableCell>
        <TableCell className="text-right">
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={onDelete}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </TableCell>
      </TableRow>

      {connection.error_message && (
        <TableRow>
          <TableCell colSpan={6} className="pt-2">
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="text-xs break-words">{connection.error_message}</AlertDescription>
            </Alert>
          </TableCell>
        </TableRow>
      )}

      {isEditingExport && (
        <TableRow>
          <TableCell colSpan={6} className="pt-2">
            <div className="space-y-2 bg-muted/50 p-4 rounded-lg border">
              <Label htmlFor={`gcp-export-${connection.id}`}>BigQuery billing export table</Label>
              <Input
                id={`gcp-export-${connection.id}`}
                value={exportTable}
                onChange={e => setExportTable(e.target.value)}
                placeholder="my-project.billing_export.gcp_billing_export_v1_XXXXXX"
                className="font-mono text-xs"
              />
              <p className="text-xs text-muted-foreground">
                Cloud console: Billing &rarr; Billing export &rarr; BigQuery export &rarr; Detailed usage cost.
                Google can take up to 24 hours to populate the first rows. Until this is set, cost blocks
                report &quot;cost data pending&quot;; non-cost automation works without it.
              </p>
              {error && <p className="text-xs text-destructive break-words">{error}</p>}
              <div className="flex flex-wrap gap-2 pt-1">
                <Button size="sm" onClick={saveExport} disabled={isSaving}>
                  {isSaving && <Loader2 className="h-3 w-3 mr-2 animate-spin" />}
                  Save
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setIsEditingExport(false);
                    setExportTable(connection.billing_export_table || '');
                    setError('');
                  }}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// GCPConnectModal walks the two-step onboarding: generate the setup script, then
// verify Kestrel can impersonate the service account it created.
function GCPConnectModal({
  open,
  onClose,
  onConnected,
}: {
  open: boolean;
  onClose: () => void;
  onConnected: () => void;
}) {
  const [step, setStep] = useState<'project' | 'script'>('project');
  const [projectId, setProjectId] = useState('');
  const [includeRemediation, setIncludeRemediation] = useState(true);
  const [bootstrap, setBootstrap] = useState<GCPBootstrapResponse | null>(null);
  const [billingExportTable, setBillingExportTable] = useState('');
  const [isWorking, setIsWorking] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  // Reset on close so reopening never shows a previous project's script.
  useEffect(() => {
    if (!open) {
      setStep('project');
      setProjectId('');
      setIncludeRemediation(true);
      setBootstrap(null);
      setBillingExportTable('');
      setError('');
      setCopied(false);
    }
  }, [open]);

  const generateScript = async () => {
    try {
      setIsWorking(true);
      setError('');
      const result = await apiClient.bootstrapGCP({
        project_id: projectId.trim(),
        include_remediation: includeRemediation,
      });
      setBootstrap(result);
      setStep('script');
    } catch (err) {
      setError(cleanErrorMessage(err));
    } finally {
      setIsWorking(false);
    }
  };

  const verify = async () => {
    if (!bootstrap) return;
    try {
      setIsWorking(true);
      setError('');
      const result = await apiClient.verifyGCPConnection({
        project_id: bootstrap.project_id,
        service_account_email: bootstrap.service_account_email,
        billing_export_table: billingExportTable.trim() || undefined,
      });
      if (result.status !== 'connected') {
        setError(result.message);
        return;
      }
      onConnected();
    } catch (err) {
      setError(cleanErrorMessage(err));
    } finally {
      setIsWorking(false);
    }
  };

  const copyScript = async () => {
    if (!bootstrap) return;
    await navigator.clipboard.writeText(bootstrap.setup_script);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Connect GCP Project</DialogTitle>
          <DialogDescription>
            Set up keyless access to your GCP project. No service-account keys are created or uploaded.
          </DialogDescription>
        </DialogHeader>

        {/* min-w-0 lets this shrink inside DialogContent's grid. Grid items
            default to min-width:auto, so without it the script block's long
            unbreakable lines widen the dialog and force horizontal scrolling. */}
        <Tabs value={step} className="w-full min-w-0">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="project" disabled={step === 'script'}>
              1. Configure
            </TabsTrigger>
            <TabsTrigger value="script" disabled={!bootstrap}>
              2. Verify
            </TabsTrigger>
          </TabsList>

          <TabsContent value="project" className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className="break-words">{error}</AlertDescription>
              </Alert>
            )}

            <div>
              <Label htmlFor="gcp-project-id">GCP Project ID</Label>
              <Input
                id="gcp-project-id"
                value={projectId}
                onChange={e => setProjectId(e.target.value)}
                placeholder="my-project-1234"
                className="font-mono mt-2"
              />
              <p className="text-xs text-muted-foreground mt-1.5">
                The project ID, not its display name or number (6&ndash;30 lowercase
                letters, digits, and hyphens).
              </p>
            </div>

            <div className="flex items-start justify-between gap-4 p-4 border rounded-lg">
              <div className="min-w-0">
                <Label htmlFor="gcp-allow-cleanup">Allow cleanup actions</Label>
                <p className="text-xs text-muted-foreground mt-1.5">
                  Grants permission to stop instances and delete unattached disks,
                  unused IPs, and old snapshots. Leave off for a read-only
                  connection; cleanup blocks will then report a missing-role error
                  instead of running.
                </p>
              </div>
              <Switch
                id="gcp-allow-cleanup"
                checked={includeRemediation}
                onCheckedChange={setIncludeRemediation}
                className="mt-0.5 shrink-0"
              />
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <Button variant="outline" onClick={onClose} disabled={isWorking}>
                Cancel
              </Button>
              <Button onClick={generateScript} disabled={isWorking || projectId.trim().length < 6}>
                {isWorking ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Preparing...
                  </>
                ) : (
                  'Generate Setup Instructions'
                )}
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="script" className="space-y-4 min-w-0">
            {error && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className="break-words">{error}</AlertDescription>
              </Alert>
            )}

            {bootstrap && (
              <>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Label>1. Run this in Cloud Shell</Label>
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" onClick={copyScript}>
                        {copied ? <Check className="h-3 w-3 mr-2" /> : <Copy className="h-3 w-3 mr-2" />}
                        {copied ? 'Copied' : 'Copy'}
                      </Button>
                      <Button variant="outline" size="sm" asChild>
                        <a href={bootstrap.cloud_shell_url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-3 w-3 mr-2" />
                          Open Cloud Shell
                        </a>
                      </Button>
                    </div>
                  </div>
                  <pre className="p-3 bg-muted rounded-lg text-xs overflow-auto max-h-64 max-w-full mt-2">
                    <code>{bootstrap.setup_script}</code>
                  </pre>
                  <p className="text-xs text-muted-foreground mt-1.5">
                    Safe to re-run. Creates{' '}
                    <code className="bg-muted px-1 py-0.5 rounded text-xs break-all">{bootstrap.service_account_email}</code>{' '}
                    and lets Kestrel impersonate it.
                  </p>
                </div>

                <div className="min-w-0">
                  <Label htmlFor="gcp-billing-export">2. BigQuery billing export (optional)</Label>
                  <Input
                    id="gcp-billing-export"
                    value={billingExportTable}
                    onChange={e => setBillingExportTable(e.target.value)}
                    placeholder="my-project.billing_export.gcp_billing_export_v1_XXXXXX"
                    className="font-mono text-xs mt-2"
                  />
                  <div className="text-xs text-muted-foreground space-y-1 mt-1.5">
                    {bootstrap.billing_export_steps.map((s, i) => (
                      <p key={i} className="break-words">{s}</p>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                  <Button variant="outline" onClick={() => setStep('project')} disabled={isWorking}>
                    Back
                  </Button>
                  <Button variant="outline" onClick={onClose} disabled={isWorking}>
                    Cancel
                  </Button>
                  <Button onClick={verify} disabled={isWorking}>
                    {isWorking ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      'Verify Connection'
                    )}
                  </Button>
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
